// This file can be replaced during build by using the `fileReplacements` array.
// `ng build --prod` replaces `environment.ts` with `environment.prod.ts`.
// The list of file replacements can be found in `angular.json`.

/**
 * @constant environment
 * @description
 * Defines the configuration settings for the **Development/Staging** environment of the AquaCloud application.
 * This object serves as the central repository for all environment-specific variables, ensuring that the application
 * can adapt to different deployment contexts (local, staging, production) without code changes.
 *
 * **Architecture & Data Flow:**
 * The application relies on a microservices architecture, and this configuration file maps the frontend
 * to the various backend services:
 * - **Core API**: Handles business logic, user management, and primary data storage.
 * - **Image Server**: A dedicated service for handling media uploads and retrieval to optimize bandwidth and storage.
 * - **Python Analytics Service**: Provides advanced machine learning models and heavy computational tasks.
 * - **PCR Service**: Specialized endpoint for Polymerase Chain Reaction data processing.
 *
 * **Key Responsibilities:**
 * 1. **Service Discovery**: Provides the base URLs for all backend microservices.
 * 2. **Feature Toggling**: The `production` flag controls development aids like source maps and strict mode.
 * 3. **Version Management**: Stores the current application version and release notes for display in the UI.
 * 4. **Third-Party Integration**: Configures the Firebase SDK for Authentication, Cloud Messaging (Push Notifications), and Analytics.
 *
 * **Build Process:**
 * This file is the default environment file used during `ng serve` and `ng build`.
 * For production builds (`ng build --prod`), Angular CLI replaces this file with `environment.prod.ts`
 * as specified in the `angular.json` fileReplacements section.
 *
 * **Security Note:**
 * While this file contains API keys (e.g., Firebase), these are generally public-facing keys.
 * Sensitive secrets should never be stored here.
 *
 * @usageNotes
 * Import this constant in services or components to access configuration values.
 *
 * @example
 * import { environment } from 'src/environments/environment';
 *
 * // Check if in production
 * if (environment.production) { ... }
 *
 * // Make an API call
 * const url = `${environment.baseUrl}/users`;
 *
 * @type {Object}
 * @author AquaCloud Development Team
 * @version 4.9.0
 */
export const environment = {
  /**
   * @property {boolean} production
   * @description Flag indicating if the application is running in production mode.
   * - `true`: Enables production optimizations (AOT, minification) and disables development assertions.
   * - `false`: Enables development mode with better debugging and stack traces.
   * @default false
   */
  production: false,

  /**
   * @property {string} hostName
   * @description The public-facing hostname of the application environment.
   * Used for generating absolute links or CORS configurations.
   */
  hostName:  'https://api-stg.aqucloud.com',

  /**
   * @property {string} baseUrl
   * @description Base URL for the main Node.js backend API.
   * All standard REST API requests (User, Farm, Pond management) are routed here.
   */
  baseUrl: 'https://api-stg.aqucloud.com/api',

  /**
   * @property {string} pcrUrl
   * @description Dedicated endpoint URL for PCR (Polymerase Chain Reaction) data services.
   * Handles specific scientific data processing separate from the main API.
   */
  pcrUrl: 'https://stage.aqucloud.com:81',

  /**
   * @property {string} imagebaseUrl
   * @description Base URL for the Image/Media server.
   * Used for uploading and retrieving static assets like profile pictures and pond images.
   */
  imagebaseUrl: 'https://api-stg.aqucloud.com:4002',

  /**
   * @property {string} newPythonBaseUrl
   * @description Base URL for the Python-based analytics and machine learning microservice.
   * Used for growth predictions, anomaly detection, and complex data analysis.
   */
  newPythonBaseUrl: 'https://api-stg.aqucloud.com:4010',

  /**
   * @property {number} port
   * @description Port number for the main backend service.
   * @default 3012
   */
  port: 3012,

  /**
   * @property {number} imageport
   * @description Port number for the image server.
   * @default 4002
   */
  imageport: 4002,

  /**
   * @property {number} newPythonPort
   * @description Port number for the Python analytics service.
   * @default 4010
   */
  newPythonPort: 4010,

  /**
   * @property {string} version
   * @description Current semantic version of the application.
   * Displayed in the UI footer or settings page.
   */
  version: '4.9.0',

  /**
   * @property {string} releaseNote
   * @description Summary of changes, features, and bug fixes in this release.
   * Used for displaying "What's New" modals or changelogs.
   */
  releaseNote: '1. Material Management functionality. 2. Bug fixes and performance improvement',

  /**
   * @property {string} releaseDate
   * @description The date when this version was released/deployed.
   * Format: DD/MM/YYYY
   */
  releaseDate: '19/05/2020',

  /**
   * @property {Object} firebase
   * @description Configuration object for Firebase services.
   * Contains public keys and identifiers required to initialize the Firebase SDK
   * for Authentication, Cloud Messaging, and Analytics.
   */
  firebase: {
    /**
     * @property {string} apiKey
     * @description Firebase API Key for identifying the project.
     */
    apiKey: "AIzaSyBaUlR8oCYKMRWxdFgxeHmp_3xulTCTKio",

    /**
     * @property {string} authDomain
     * @description Domain for Firebase Authentication redirects.
     */
    authDomain: "aqu-apn.firebaseapp.com",

    /**
     * @property {string} projectId
     * @description Unique identifier for the Firebase project.
     */
    projectId: "aqu-apn",

    /**
     * @property {string} storageBucket
     * @description Google Cloud Storage bucket for Firebase Storage.
     */
    storageBucket: "aqu-apn.appspot.com",

    /**
     * @property {string} messagingSenderId
     * @description Sender ID for Firebase Cloud Messaging (FCM).
     * Used to identify the app server when sending push notifications.
     */
    messagingSenderId: "662032625792",

    /**
     * @property {string} appId
     * @description Unique identifier for the Firebase App within the project.
     */
    appId: "1:662032625792:web:9ffc3491c89db48c33f665",

    /**
     * @property {string} measurementId
     * @description ID for Google Analytics measurement.
     */
    measurementId: "G-WVWG37WJM8",

    /**
     * @property {string} vapidKey
     * @description Voluntary Application Server Identification (VAPID) key.
     * Public key used for web push notifications authentication.
     */
    vapidKey: 'BNYx1YxCJPI8Pvdb5kiGGIrTSNZAq7IGtEPcadxSmMfu0_hGvhjGmxmZ6NSySx97j7Y3Jd0Y2_CqkVmYzGYFKPQ'
  }
};

/*
 * For easier debugging in development mode, you can import the following file
 * to ignore zone related error stack frames such as `zone.run`, `zoneDelegate.invokeTask`.
 *
 * This import should be commented out in production mode because it will have a negative impact
 * on performance if an error is thrown.
 */
// import 'zone.js/plugins/zone-error';  // Included with Angular CLI.
